import svgPaths from "./svg-paths";
import toolsSvgPaths from "./svg-tools-paths";
import { useState, useRef, useEffect, useCallback, type ReactNode } from "react";
import imgEllipse3 from "./assets/2e942a94fde67460ac848fe73ce91d0871df6c8b.png";
import imgEllipse1 from "./assets/baff0ede1a84dbc0b053b442efaca24e982e9c19.png";
import imgEllipse2 from "./assets/d797a0a87c6db5806685365afec729a16032873c.png";
import imgEllipse4 from "./assets/4108c19ddd25f3da31c799598a91dc3d7fa99ff8.png";
import imgEllipse5 from "./assets/970a207323a1a658dd60a5b80700cccec612fe7c.png";
import imgEllipse6 from "./assets/cfd54ce515d63bfc923bc64a1a5ecb63bb7dac04.png";
import { HomepageTiltSlab } from "./HomepageTiltSlab";
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  detectAppPlatform,
  type AppPlatform,
} from "./appLinks";

const DISCORD_URL = "https://discord.gg/XTxUgUAFJh";

function ArrowDown() {
  return (
    <div className="relative size-[12px]" data-name="arrow-down-01">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 12 12"
      >
        <g id="arrow-down-01">
          <path
            clipRule="evenodd"
            d={svgPaths.p32aef200}
            fill="var(--fill-0, #07001A)"
            fillRule="evenodd"
            id="Vector"
          />
        </g>
      </svg>
    </div>
  );
}

function AppleIcon({ large = false }: { large?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={`block shrink-0 ${large ? "size-[28px]" : "size-[22px]"}`}
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M16.36 1.86c.02 1.7-1.05 3.23-2.33 4.04-1.36.86-2.67.72-2.75.72-.02-1.58.86-3.12 2.06-4.02 1.16-.87 2.62-.78 3.02-.74ZM20.24 17.56c-.48 1.1-.72 1.6-1.34 2.56-.87 1.33-2.1 2.98-3.63 3-1.35.01-1.7-.88-3.54-.87-1.84 0-2.23.89-3.58.88-1.53-.01-2.69-1.5-3.56-2.83-2.43-3.72-2.68-8.08-1.18-10.4 1.07-1.65 2.75-2.62 4.33-2.62 1.6 0 2.61.88 3.94.88 1.29 0 2.08-.88 3.94-.88 1.41 0 2.91.77 3.97 2.1-3.49 1.91-2.92 6.89.65 8.18Z" />
    </svg>
  );
}

function GooglePlayIcon({ large = false }: { large?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={`block shrink-0 ${large ? "size-[28px]" : "size-[22px]"}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <path d="M4.5 3.4v17.2l9.2-8.6-9.2-8.6Z" fill="#34A853" />
      <path d="m13.7 12 2.7-2.5L5.4 3c-.33-.2-.64-.24-.9-.12l9.2 9.12Z" fill="#FBBC04" />
      <path d="m13.7 12-9.2 9.12c.26.12.57.08.9-.12l11-6.5-2.7-2.5Z" fill="#4285F4" />
      <path d="m16.4 9.5-2.7 2.5 2.7 2.5 2.23-1.32c1.16-.69 1.16-1.67 0-2.36L16.4 9.5Z" fill="#EA4335" />
    </svg>
  );
}

type StoreLinkVariant = "darkBadge" | "lightBadge" | "menu";

function useDetectedAppPlatform(): AppPlatform {
  const [platform, setPlatform] = useState<AppPlatform>(() => detectAppPlatform());

  useEffect(() => {
    setPlatform(detectAppPlatform());
  }, []);

  return platform;
}

function StoreLink({
  href,
  icon,
  kicker,
  label,
  large = false,
  variant = "darkBadge",
}: {
  href: string;
  icon: ReactNode;
  kicker: string;
  label: string;
  large?: boolean;
  variant?: StoreLinkVariant;
}) {
  const className =
    variant === "menu"
      ? "bg-white content-stretch flex gap-[12px] items-center p-[12px] relative rounded-[12px] w-full no-underline text-[#07001a] shadow-[0px_3px_3.5px_0px_rgba(0,0,0,0.05)] transition-transform duration-150 hover:scale-[1.02] active:scale-[0.99]"
      : variant === "lightBadge"
        ? `bg-[#fafafa] content-stretch flex gap-[10px] items-center justify-center ${large ? "min-w-[260px] px-[22px] py-[15px] rounded-[18px]" : "min-w-[164px] px-[14px] py-[10px] rounded-[12px]"} relative shrink-0 no-underline text-[#07001a] transition-transform duration-150 hover:scale-[1.02] active:scale-[0.99]`
        : `bg-[#141221] content-stretch flex gap-[10px] items-center justify-center ${large ? "min-w-[260px] px-[22px] py-[15px] rounded-[18px]" : "min-w-[164px] px-[14px] py-[10px] rounded-[12px]"} relative shrink-0 no-underline text-[#fafafa] transition-transform duration-150 hover:scale-[1.02] active:scale-[0.99]`;

  return (
    <a className={className} href={href} rel="noreferrer" target="_blank">
      {icon}
      <span className="content-stretch flex flex-col items-start leading-none relative shrink-0">
        <span className={`font-['Figtree',sans-serif] font-semibold ${large ? "text-[13px]" : "text-[10px]"} leading-[1.1] opacity-80 whitespace-nowrap`}>
          {kicker}
        </span>
        <span className={`font-['Figtree',sans-serif] font-black ${large ? "text-[24px]" : "text-[17px]"} leading-[1.15] whitespace-nowrap`}>
          {label}
        </span>
      </span>
    </a>
  );
}

export function StoreButtons({ variant = "darkBadge" }: { variant?: StoreLinkVariant }) {
  const platform = useDetectedAppPlatform();
  const platformStore =
    platform === "ios"
      ? {
          href: APP_STORE_URL,
          icon: <AppleIcon large />,
          kicker: "Download Evo on the",
          label: "App Store",
        }
      : platform === "android"
        ? {
            href: GOOGLE_PLAY_URL,
            icon: <GooglePlayIcon large />,
            kicker: "Download Evo on",
            label: "Google Play",
          }
        : null;

  if (platformStore) {
    return (
      <div className="content-stretch flex items-center justify-center lg:justify-start relative shrink-0">
        <StoreLink {...platformStore} large variant={variant} />
      </div>
    );
  }

  return (
    <div className="content-stretch flex flex-col sm:flex-row gap-[10px] items-center justify-center lg:justify-start relative shrink-0">
      <StoreLink
        href={APP_STORE_URL}
        icon={<AppleIcon />}
        kicker="Download on the"
        label="App Store"
        variant={variant}
      />
      <StoreLink
        href={GOOGLE_PLAY_URL}
        icon={<GooglePlayIcon />}
        kicker="Get it on"
        label="Google Play"
        variant={variant}
      />
    </div>
  );
}

function DiscordCommunityLink({ dark = false }: { dark?: boolean }) {
  return (
    <a
      className={`content-stretch flex gap-[8px] items-center justify-center px-[12px] py-[6px] relative rounded-[20px] shrink-0 no-underline transition-opacity hover:opacity-80 ${
        dark ? "text-[#fafafa]" : "text-[#07001a]"
      }`}
      href={DISCORD_URL}
      rel="noreferrer"
      target="_blank"
    >
      <span className="bg-[#5865f2] content-stretch flex items-center justify-center rounded-full size-[24px] shrink-0">
        <DiscordIconSvgrepoCom />
      </span>
      <span className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[14px] whitespace-nowrap">
        Join the Discord
      </span>
    </a>
  );
}

function ToolsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative shrink-0 z-[9999]" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white content-stretch cursor-pointer flex gap-[6px] items-center justify-center px-[12px] py-[6px] relative rounded-[12px] shadow-[0px_3px_3.5px_0px_rgba(0,0,0,0.05)] transition-transform duration-150 hover:scale-[1.02] active:scale-[0.99]"
      >
        <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[#07001a] text-[14px] whitespace-nowrap">
          Tools
        </p>
        <div className="flex items-center justify-center relative shrink-0">
          <div
            className={`flex-none transition-transform duration-200 ${isOpen ? "" : "rotate-180"}`}
          >
            <ArrowDown />
          </div>
        </div>
      </button>
      {isOpen && (
        <div
          className="fixed sm:absolute right-[12px] sm:right-0 top-auto sm:top-[calc(100%+6px)] z-[9999]"
          style={{ marginTop: 6 }}
        >
          <div className="bg-white rounded-[24px] shadow-[0px_8px_24px_rgba(0,0,0,0.12)] w-[465px] max-w-[calc(100vw-24px)]">
            <div className="flex flex-col gap-[12px] p-[12px]">
              <button
                className="bg-white content-stretch flex items-center p-[12px] relative rounded-[12px] w-full cursor-pointer transition-transform duration-150 hover:scale-[1.02] active:scale-[0.99]"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = "/grading-plays";
                }}
              >
                <div
                  aria-hidden="true"
                  className="absolute border border-[rgba(0,6,20,0.05)] border-solid inset-0 pointer-events-none rounded-[12px] shadow-[0px_3px_3.5px_0px_rgba(0,0,0,0.05)]"
                />
                <div className="content-stretch flex flex-[1_0_0] gap-[12px] items-center min-h-px min-w-px relative">
                  <div className="relative shrink-0 size-[48px]">
                    <svg
                      className="absolute block size-full"
                      fill="none"
                      preserveAspectRatio="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d={toolsSvgPaths.p2e37d000}
                        stroke="#44B300"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="5"
                      />
                      <path
                        d={toolsSvgPaths.p37d87880}
                        stroke="#44B300"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="5"
                      />
                      <path
                        d="M24 13V16M24 35V32"
                        stroke="#44B300"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="5"
                      />
                    </svg>
                  </div>
                  <div className="content-stretch flex flex-[1_0_0] flex-col items-start min-h-px min-w-px relative text-left">
                    <p className="font-['Figtree',sans-serif] font-black leading-[1.5] text-[#07001a] text-[16px] tracking-[-0.32px] whitespace-nowrap">
                      Grading plays
                    </p>
                    <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] text-[#776f7b] text-[14px]">
                      Sort cards by expected grading profit to find the
                      most profitable cards to grade
                    </p>
                  </div>
                </div>
              </button>
              <div className="bg-white content-stretch flex gap-[12px] items-center p-[12px] relative rounded-[12px] w-full cursor-default transition-transform duration-150 hover:scale-[1.02]">
                <div
                  aria-hidden="true"
                  className="absolute border border-[rgba(0,6,20,0.05)] border-solid inset-0 pointer-events-none rounded-[12px] shadow-[0px_3px_3.5px_0px_rgba(0,0,0,0.05)]"
                />
                <div className="relative shrink-0 size-[48px]">
                  <svg
                    className="absolute block size-full"
                    fill="none"
                    preserveAspectRatio="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d={toolsSvgPaths.pd421820}
                      stroke="#B3AFB6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="5"
                    />
                    <path
                      d={toolsSvgPaths.p2ef2f00}
                      stroke="#B3AFB6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="5"
                    />
                    <path
                      d="M12 24L16 26"
                      stroke="#B3AFB6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="5"
                    />
                    <path
                      d="M34 8L14 18"
                      stroke="#B3AFB6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="5"
                    />
                  </svg>
                </div>
                <div className="content-stretch flex flex-col items-start leading-[1.5] relative shrink-0">
                  <div className="content-stretch flex font-['Figtree',sans-serif] font-black gap-[6px] items-center relative shrink-0 whitespace-nowrap">
                    <p className="relative shrink-0 text-[#b3afb6] text-[16px] tracking-[-0.32px]">
                      Submission tracker
                    </p>
                    <p className="relative shrink-0 text-[#ff381a] text-[12px] tracking-[-0.24px]">
                      Coming soon
                    </p>
                  </div>
                  <p className="font-['Figtree',sans-serif] font-semibold relative shrink-0 text-[#b3afb6] text-[14px] w-[238px]">
                    Organize and keep track of your grading submissions
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HeaderGetAppDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative shrink-0 z-[9999]" ref={dropdownRef}>
      <button
        aria-expanded={isOpen}
        className="bg-[#ff381a] content-stretch cursor-pointer flex items-center justify-center px-[12px] py-[6px] relative rounded-[12px] shrink-0 transition-transform duration-150 hover:scale-[1.02] active:scale-[0.99]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[14px] text-white whitespace-nowrap">
          Get app
        </p>
      </button>
      {isOpen && (
        <div
          className="fixed sm:absolute right-[12px] sm:right-0 top-auto sm:top-[calc(100%+6px)] z-[9999]"
          style={{ marginTop: 6 }}
        >
          <div className="bg-white rounded-[24px] shadow-[0px_8px_24px_rgba(0,0,0,0.12)] w-[280px] max-w-[calc(100vw-24px)]">
            <div className="flex flex-col gap-[10px] p-[12px]">
              <StoreLink
                href={APP_STORE_URL}
                icon={<AppleIcon />}
                kicker="Download on the"
                label="App Store"
                variant="menu"
              />
              <StoreLink
                href={GOOGLE_PLAY_URL}
                icon={<GooglePlayIcon />}
                kicker="Get it on"
                label="Google Play"
                variant="menu"
              />
              <a
                className="bg-white content-stretch flex gap-[12px] items-center p-[12px] relative rounded-[12px] w-full no-underline text-[#07001a] shadow-[0px_3px_3.5px_0px_rgba(0,0,0,0.05)] transition-transform duration-150 hover:scale-[1.02] active:scale-[0.99]"
                href={DISCORD_URL}
                rel="noreferrer"
                target="_blank"
              >
                <span className="bg-[#5865f2] content-stretch flex items-center justify-center rounded-full size-[36px] shrink-0">
                  <DiscordIconSvgrepoCom />
                </span>
                <span className="content-stretch flex flex-col items-start relative shrink-0">
                  <span className="font-['Figtree',sans-serif] font-black leading-[1.3] text-[16px] whitespace-nowrap">
                    Discord
                  </span>
                  <span className="font-['Figtree',sans-serif] font-semibold leading-[1.35] text-[#776f7b] text-[12px] whitespace-nowrap">
                    Join the collector community
                  </span>
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Frame8() {
  return (
    <div className="content-stretch flex gap-[12px] items-center justify-end relative shrink-0">
      <ToolsDropdown />
      <HeaderGetAppDropdown />
    </div>
  );
}

export function WebsiteHeader() {
  return (
    <div className="relative rounded-[24px] shrink-0 w-full max-w-[1440px] mx-auto px-[24px] box-border">
      <div className="flex flex-row items-center rounded-[inherit] size-full">
        <div className="content-stretch flex items-center justify-between py-[24px] relative w-full">
          <div
            className="h-[24px] relative shrink-0 w-[76.8px]"
            data-name="evo-full"
          >
            <svg
              className="absolute block size-full"
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 76.8 24"
            >
              <g id="evo-full">
                <path d={svgPaths.p22369200} fill="var(--fill-0, black)" />
                <path d={svgPaths.p369aad00} fill="var(--fill-0, black)" />
                <path d={svgPaths.p3fd92480} fill="var(--fill-0, black)" />
                <path d={svgPaths.p1a0fa080} fill="var(--fill-0, black)" />
                <path d={svgPaths.pf751c0} fill="var(--fill-0, black)" />
              </g>
            </svg>
          </div>
          <Frame8 />
        </div>
      </div>
    </div>
  );
}

function Frame2() {
  return (
    <div className="content-stretch flex flex-col gap-[10px] items-center lg:items-start justify-center relative shrink-0">
      <StoreButtons />
      <DiscordCommunityLink />
    </div>
  );
}

function Frame7() {
  return (
    <div className="content-stretch flex isolate items-start pr-[12px] relative shrink-0">
      <div className="mr-[-12px] relative shrink-0 size-[24px] z-[3]">
        <div className="absolute inset-[-2.08%_-14.58%_-27.08%_-14.58%]">
          <img
            alt=""
            className="block max-w-none size-full"
            height="31"
            src={imgEllipse3}
            width="31"
          />
        </div>
      </div>
      <div className="mr-[-12px] relative shrink-0 size-[24px] z-[2]">
        <div className="absolute inset-[-2.08%_-14.58%_-27.08%_-14.58%]">
          <img
            alt=""
            className="block max-w-none size-full"
            height="31"
            src={imgEllipse1}
            width="31"
          />
        </div>
      </div>
      <div className="mr-[-12px] relative shrink-0 size-[24px] z-[1]">
        <div className="absolute inset-[-2.08%_-14.58%_-27.08%_-14.58%]">
          <img
            alt=""
            className="block max-w-none size-full"
            height="31"
            src={imgEllipse2}
            width="31"
          />
        </div>
      </div>
    </div>
  );
}

function Frame6() {
  return (
    <div className="content-stretch flex flex-col sm:flex-row gap-[12px] items-center justify-center relative shrink-0 flex-wrap">
      <Frame7 />
      <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[#b3afb6] text-[14px] max-w-[339px] text-center sm:text-left">
        Join thousands of other collectors using Evo to grade cards, track market
        prices, and manage their collection
      </p>
    </div>
  );
}

function Frame12() {
  return (
    <div className="content-stretch flex flex-col gap-[12px] items-center lg:items-start justify-center relative shrink-0">
      <Frame2 />
      <Frame6 />
    </div>
  );
}

function Frame9() {
  return (
    <div className="content-stretch flex flex-[1_0_0] flex-col gap-[48px] items-center lg:items-start justify-center min-h-px min-w-px relative">
      <p className="font-['Figtree',sans-serif] font-black leading-[1.5] max-w-[calc(100vw-48px)] lg:max-w-[600px] relative shrink-0 text-[clamp(32px,5vw,64px)] text-black tracking-[-1.28px] text-center lg:text-left">
        <span className="block">Predict your next</span>
        <span className="block">PSA submission</span>
      </p>
      <Frame12 />
    </div>
  );
}

function Frame17() {
  return (
    <div className="content-stretch flex flex-[1_0_0] items-start min-h-px min-w-px relative">
      <Frame9 />
    </div>
  );
}

function Frame24() {
  return (
    <div className="homepage-slab-shell relative shrink-0 mr-0 lg:mr-[48px]">
      <HomepageTiltSlab className="homepage-demo-slab" />
    </div>
  );
}

function Frame25() {
  return (
    <div className="content-stretch flex flex-col lg:flex-row items-center lg:items-start justify-between max-w-[1440px] py-[48px] px-[24px] relative shrink-0 w-full gap-[32px] box-border overflow-hidden">
      <Frame17 />
      <Frame24 />
    </div>
  );
}

function FrameHero() {
  return (
    <div className="bg-[#f8f7f6] content-stretch flex flex-col items-center relative shrink-0 w-full">
      <WebsiteHeader />
      <Frame25 />
    </div>
  );
}

function Frame18() {
  return (
    <div className="content-stretch flex items-center justify-center lg:justify-start max-w-[1440px] relative shrink-0 w-full">
      <p className="font-['Figtree',sans-serif] font-black leading-[1.3] relative text-[#fafafa] text-[clamp(22px,3vw,32px)] tracking-[-0.64px] text-center lg:text-left">
        Evo is an AI grading and collection management app for Pokémon card
        collectors
      </p>
    </div>
  );
}

function ArrowLeftIcon({ opacity = 1 }: { opacity?: number }) {
  return (
    <div className="relative size-[24px]" data-name="arrow-left-02">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 24 24"
      >
        <g id="arrow-left-02">
          <path
            d={svgPaths.p321ff100}
            id="Vector"
            stroke="#FAFAFA"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeOpacity={opacity}
            strokeWidth="3"
          />
        </g>
      </svg>
    </div>
  );
}

function Frame42({
  activeIndex,
  onPrev,
  onNext,
  total,
}: {
  activeIndex: number;
  onPrev: () => void;
  onNext: () => void;
  total: number;
}) {
  const leftOpacity = activeIndex === 0 ? 0.5 : 1;

  return (
    <div className="content-stretch flex items-center relative shrink-0">
      <button
        onClick={onPrev}
        className="content-stretch flex items-center p-[12px] relative rounded-[24px] cursor-pointer transition-all duration-150 hover:scale-[1.02] active:scale-[0.99]"
      >
        <div className="flex items-center justify-center relative shrink-0">
          <ArrowLeftIcon opacity={leftOpacity} />
        </div>
      </button>
      <div className="content-stretch flex items-center justify-center p-[3px] relative rounded-[24px] shrink-0 w-[40px]">
        <div className="flex flex-col font-['Figtree',sans-serif] font-black justify-center leading-[0] relative shrink-0 text-[#fafafa] text-[16px] tracking-[-0.32px] whitespace-nowrap text-center">
          <p className="leading-[1.5]">
            {activeIndex + 1}/{total}
          </p>
        </div>
      </div>
      <button
        onClick={onNext}
        className="content-stretch flex items-center p-[12px] relative rounded-[24px] shrink-0 cursor-pointer transition-all duration-150 hover:scale-[1.02] active:scale-[0.99]"
      >
        <div className="flex items-center justify-center relative shrink-0">
          <div className="flex-none rotate-180">
            <ArrowLeftIcon opacity={1} />
          </div>
        </div>
      </button>
    </div>
  );
}

const SLIDES = [
  {
    src: "/carousel-gradecard.png",
    subtitle: "Receive a data-backed grade prediction for any trading card, in seconds",
  },
  {
    src: "/carousel-gradedetails.png",
    subtitle: "See the centering, damage, and profit data behind each grade",
  },
  {
    src: "/carousel-plays.png",
    subtitle: "Find grading plays with the strongest upside",
  },
  {
    src: "/carousel-submit.png",
    subtitle: "Track submissions, returns, and expected profit after fees",
  },
  {
    src: "/carousel-collection.png",
    subtitle: "View your collection's total value and top movers over time",
  },
  {
    src: "/carousel-illustrator.png",
    subtitle: "Explore market data by artist, card, and trend",
  },
];

function Frame13({
  activeIndex,
  onPrev,
  onNext,
  total,
}: {
  activeIndex: number;
  onPrev: () => void;
  onNext: () => void;
  total: number;
}) {
  return (
    <div className="content-stretch flex flex-col sm:flex-row items-center sm:items-center justify-between relative shrink-0 w-full gap-[12px]">
      <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[#fafafa] text-[16px] max-w-[400px] text-center sm:text-left">
        {SLIDES[activeIndex].subtitle}
      </p>
      <Frame42
        activeIndex={activeIndex}
        onPrev={onPrev}
        onNext={onNext}
        total={total}
      />
    </div>
  );
}

function PhoneSlide({
  children,
  isActive,
  onClick,
  isHovered,
}: {
  children: React.ReactNode;
  isActive: boolean;
  onClick?: () => void;
  isHovered?: boolean;
}) {
  const [localHover, setLocalHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const showFullOpacity = isActive || localHover || isHovered;

  return (
    <div
      className="shrink-0 relative transition-all duration-500 ease-in-out cursor-pointer"
      style={{
        opacity: showFullOpacity ? 1 : 0.5,
        transform: `scale(${isActive ? (window.innerWidth < 640 ? 1.08 : 1.16) : 1}) scale(${pressed ? 0.99 : localHover ? 1.02 : 1})`,
        transformOrigin: "center center",
        zIndex: isActive ? 2 : localHover ? 3 : 1,
        margin: isActive
          ? window.innerWidth < 640
            ? "0 12px"
            : "0 24px"
          : "0",
      }}
      onClick={onClick}
      onMouseEnter={() => setLocalHover(true)}
      onMouseLeave={() => {
        setLocalHover(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
    >
      <div className="h-[420px] sm:h-[587.377px] pointer-events-none relative rounded-[30px] sm:rounded-[43.074px] shrink-0 w-[194px] sm:w-[271px] transition-all duration-500">
        {children}
        <div
          aria-hidden="true"
          className="absolute border-solid rounded-[36px] sm:rounded-[49.074px] transition-all duration-500 p-[0px] m-[0px]"
          style={{
            borderWidth: isActive ? "6px" : "2px",
            borderColor: "#241e2b",
            inset: isActive ? "-6px" : "-2px",
          }}
        />
      </div>
    </div>
  );
}

function CarouselPhoneSlide({
  src,
  isActive,
  onClick,
}: {
  src: string;
  isActive: boolean;
  onClick?: () => void;
}) {
  return (
    <PhoneSlide isActive={isActive} onClick={onClick}>
      <img
        alt=""
        className="absolute inset-0 object-contain rounded-[30px] sm:rounded-[43.074px] size-full"
        src={src}
      />
    </PhoneSlide>
  );
}

function Frame26({
  activeIndex,
  onSwipe,
  onSlideClick,
}: {
  activeIndex: number;
  onSwipe: (dir: number) => void;
  onSlideClick: (i: number) => void;
}) {
  const isSmall = typeof window !== "undefined" && window.innerWidth < 640;
  const SLIDE_WIDTH = isSmall ? 194 : 271;
  const GAP = isSmall ? 16 : 24;
  const containerRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const didDrag = useRef(false);
  const lastX = useRef(0);
  const lastTime = useRef(0);
  const velocity = useRef(0);
  const inertiaAnim = useRef<number>(0);
  const [inertiaOffset, setInertiaOffset] = useState(0);
  const inertiaOffsetRef = useRef(0);

  const cancelInertia = () => {
    if (inertiaAnim.current) {
      cancelAnimationFrame(inertiaAnim.current);
      inertiaAnim.current = 0;
    }
    inertiaOffsetRef.current = 0;
    setInertiaOffset(0);
  };

  const startDrag = (x: number) => {
    cancelInertia();
    startX.current = x;
    lastX.current = x;
    lastTime.current = Date.now();
    velocity.current = 0;
    isDragging.current = true;
    didDrag.current = false;
    dragOffsetRef.current = 0;
  };

  const moveDrag = (x: number) => {
    if (!isDragging.current) return;
    const now = Date.now();
    const dt = now - lastTime.current;
    if (dt > 0) {
      velocity.current = (x - lastX.current) / dt;
    }
    lastX.current = x;
    lastTime.current = now;
    const delta = x - startX.current;
    dragOffsetRef.current = delta;
    setDragOffset(delta);
    if (Math.abs(delta) > 5) didDrag.current = true;
  };

  const endDrag = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const d = dragOffsetRef.current;
    const v = velocity.current;

    dragOffsetRef.current = 0;
    setDragOffset(0);

    const absV = Math.abs(v);
    if (absV > 0.3 || Math.abs(d) > 50) {
      const slidesToSkip = absV > 0.8 ? 2 : 1;
      const dir = v !== 0 ? (v > 0 ? -1 : 1) : d > 0 ? -1 : 1;

      const inertiaDistance = dir * -(SLIDE_WIDTH * 0.3);
      inertiaOffsetRef.current = inertiaDistance;
      setInertiaOffset(inertiaDistance);

      const startInertia = inertiaDistance;
      const duration = 300;
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = startInertia * (1 - eased);
        inertiaOffsetRef.current = current;
        setInertiaOffset(current);
        if (progress < 1) {
          inertiaAnim.current = requestAnimationFrame(animate);
        } else {
          inertiaAnim.current = 0;
          inertiaOffsetRef.current = 0;
          setInertiaOffset(0);
        }
      };
      inertiaAnim.current = requestAnimationFrame(animate);

      for (let i = 0; i < slidesToSkip; i++) {
        onSwipe(dir);
      }
    }
  };

  const handleTouchStart = (e: React.TouchEvent) =>
    startDrag(e.touches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) =>
    moveDrag(e.touches[0].clientX);
  const handleTouchEnd = () => endDrag();
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    startDrag(e.clientX);
  };
  const handleMouseMove = (e: React.MouseEvent) => moveDrag(e.clientX);
  const handleMouseUp = () => endDrag();

  const wheelTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const wheelAccum = useRef(0);
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      const delta =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      wheelAccum.current += delta;
      if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
      wheelTimeout.current = setTimeout(() => {
        if (Math.abs(wheelAccum.current) > 40) {
          onSwipe(wheelAccum.current > 0 ? 1 : -1);
        }
        wheelAccum.current = 0;
      }, 100);
    },
    [onSwipe],
  );

  const baseOffset = activeIndex * (SLIDE_WIDTH + GAP);
  const leftPeek = activeIndex > 0 ? (isSmall ? 80 : 160) : 0;
  const totalTranslate = -baseOffset + leftPeek + dragOffset + inertiaOffset;

  return (
    <div
      ref={containerRef}
      className="relative shrink-0 w-full select-none overflow-visible p-[0px]"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      <div
        className="flex gap-[16px] sm:gap-[24px] items-center px-[16px] sm:px-[30px] py-[40px] sm:py-[60px] relative"
        style={{
          transform: `translateX(${totalTranslate}px)`,
          transition: isDragging.current
            ? "none"
            : "transform 0.5s ease-in-out",
        }}
      >
        {SLIDES.map((slide, i) => (
          <CarouselPhoneSlide
            key={slide.src}
            src={slide.src}
            isActive={i === activeIndex}
            onClick={() => {
              if (!didDrag.current) onSlideClick(i);
            }}
          />
        ))}
        <div className="shrink-0 flex items-center justify-center h-[420px] sm:h-[587.377px]">
          <button
            onClick={() => {
              if (!didDrag.current) onSlideClick(0);
            }}
            className="bg-[#23212b] relative content-stretch flex items-center justify-center px-[12px] py-[6px] rounded-[12px] cursor-pointer transition-all duration-150 hover:scale-[1.02] active:scale-[0.99]"
          >
            <div
              aria-hidden="true"
              className="absolute border border-[rgba(250,250,250,0.05)] border-solid inset-0 pointer-events-none rounded-[12px] shadow-[0px_3px_3.5px_0px_rgba(0,0,0,0.05)]"
            />
            <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[#fafafa] text-[14px] whitespace-nowrap">
              Back to front
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}

function Frame36() {
  const total = SLIDES.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const onPrev = () => setActiveIndex((prev) => (prev - 1 + total) % total);
  const onNext = () => setActiveIndex((prev) => (prev + 1) % total);
  const onSwipe = useCallback(
    (dir: number) => {
      setActiveIndex((prev) => {
        const next = prev + dir;
        if (next < 0) return total - 1;
        if (next >= total) return 0;
        return next;
      });
    },
    [total],
  );
  const onSlideClick = useCallback((i: number) => {
    setActiveIndex(i);
  }, []);

  return (
    <div className="content-stretch flex flex-col gap-[24px] items-start justify-center relative shrink-0 w-full max-w-[1440px]">
      <div className="relative z-20 w-full">
        <Frame13
          activeIndex={activeIndex}
          onPrev={onPrev}
          onNext={onNext}
          total={total}
        />
      </div>
      <div className="relative w-full overflow-x-hidden overflow-y-visible">
        <Frame26
          activeIndex={activeIndex}
          onSwipe={onSwipe}
          onSlideClick={onSlideClick}
        />
        {activeIndex > 0 && (
          <div
            className="absolute left-0 top-0 bottom-0 w-[40px] pointer-events-none z-10"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgb(20, 18, 33) 0%, rgba(20, 18, 33, 0) 100%)",
            }}
          />
        )}
        <div
          className="absolute right-0 top-0 bottom-0 w-[151px] pointer-events-none z-10"
          style={{
            backgroundImage:
              "linear-gradient(to left, rgb(20, 18, 33) 0%, rgba(20, 18, 33, 0) 100%)",
          }}
        />
      </div>
    </div>
  );
}

function Frame10() {
  return (
    <div className="content-stretch flex flex-col gap-[10px] items-center sm:items-end justify-center relative shrink-0">
      <StoreButtons variant="lightBadge" />
      <DiscordCommunityLink dark />
    </div>
  );
}

function Frame14() {
  return (
    <div className="content-stretch flex isolate items-start pr-[12px] relative shrink-0">
      <div className="mr-[-12px] relative shrink-0 size-[24px] z-[3]">
        <div className="absolute inset-[-2.08%_-14.58%_-27.08%_-14.58%]">
          <img
            alt=""
            className="block max-w-none size-full"
            height="31"
            src={imgEllipse4}
            width="31"
          />
        </div>
      </div>
      <div className="mr-[-12px] relative shrink-0 size-[24px] z-[2]">
        <div className="absolute inset-[-2.08%_-14.58%_-27.08%_-14.58%]">
          <img
            alt=""
            className="block max-w-none size-full"
            height="31"
            src={imgEllipse5}
            width="31"
          />
        </div>
      </div>
      <div className="mr-[-12px] relative shrink-0 size-[24px] z-[1]">
        <div className="absolute inset-[-2.08%_-14.58%_-27.08%_-14.58%]">
          <img
            alt=""
            className="block max-w-none size-full"
            height="31"
            src={imgEllipse6}
            width="31"
          />
        </div>
      </div>
    </div>
  );
}

function Frame11() {
  return (
    <div className="content-stretch flex gap-[12px] items-center justify-center relative shrink-0">
      <Frame14 />
      <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[#8b838f] text-[14px] whitespace-nowrap">
        Join thousands of other collectors using Evo
      </p>
    </div>
  );
}

function Frame37() {
  return (
    <div className="content-stretch flex flex-col gap-[12px] items-center sm:items-end justify-center relative shrink-0 w-full">
      <Frame10 />
      <Frame11 />
    </div>
  );
}

function Frame32() {
  return (
    <div className="content-stretch flex flex-col gap-[48px] items-start justify-center max-w-[1440px] relative shrink-0 w-full">
      <Frame18 />
      <Frame36 />
      <Frame37 />
    </div>
  );
}

function Frame3() {
  return (
    <div className="bg-[#141221] relative shrink-0 w-full overflow-x-hidden overflow-y-visible">
      <div className="flex flex-col items-center size-full">
        <div className="content-stretch flex flex-col items-center px-[24px] py-[48px] relative w-full box-border">
          <Frame32 />
        </div>
      </div>
    </div>
  );
}

function Frame35() {
  return (
    <div className="h-[30px] relative shrink-0 w-[105.6px]">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 105.6 30"
      >
        <g id="Frame 1343">
          <g id="evo-full">
            <path d={svgPaths.p17c40c00} fill="var(--fill-0, white)" />
            <path d={svgPaths.p3f0f4200} fill="var(--fill-0, white)" />
            <path d={svgPaths.p2e034c00} fill="var(--fill-0, white)" />
            <path d={svgPaths.p1e156e00} fill="var(--fill-0, white)" />
            <path d={svgPaths.p15637080} fill="var(--fill-0, white)" />
          </g>
        </g>
      </svg>
    </div>
  );
}

function Group() {
  return (
    <div className="absolute inset-[11.13%_0_11.46%_0]" data-name="Group">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 18 13.9338"
      >
        <g id="Group">
          <path d={svgPaths.pd84a880} fill="var(--fill-0, white)" id="Vector" />
        </g>
      </svg>
    </div>
  );
}

function DiscordIconSvgrepoCom() {
  return (
    <div
      className="overflow-clip relative shrink-0 size-[18px]"
      data-name="discord-icon-svgrepo-com 1"
    >
      <Group />
    </div>
  );
}

function Frame20() {
  return (
    <a
      className="content-stretch flex gap-[12px] items-center px-[12px] py-[6px] relative rounded-[20px] shrink-0 no-underline hover:opacity-80"
      href={DISCORD_URL}
      rel="noreferrer"
      target="_blank"
    >
      <DiscordIconSvgrepoCom />
      <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap">
        Join our Discord server
      </p>
    </a>
  );
}

function Mail() {
  return (
    <div className="relative shrink-0 size-[18px]" data-name="mail-01">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 18 18"
      >
        <g id="mail-01">
          <path
            d={svgPaths.p3ae5cc00}
            id="Vector"
            stroke="var(--stroke-0, white)"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
          <path
            d={svgPaths.p3e25a540}
            id="Vector_2"
            stroke="var(--stroke-0, white)"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </g>
      </svg>
    </div>
  );
}

function Frame21() {
  return (
    <a
      href="mailto:hello@evograding.com"
      className="content-stretch flex gap-[12px] items-center px-[12px] py-[6px] relative rounded-[20px] shrink-0 no-underline hover:opacity-80"
    >
      <Mail />
      <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap">
        hello@evograding.com
      </p>
    </a>
  );
}

function Frame19() {
  return (
    <div className="content-stretch flex flex-col sm:flex-row items-center gap-[4px] sm:gap-0 relative shrink-0">
      <Frame35 />
      <Frame20 />
      <Frame21 />
    </div>
  );
}

function LegalLinks() {
  return (
    <div className="content-stretch flex flex-wrap items-center justify-center gap-[16px] relative shrink-0">
      <a
        className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap no-underline hover:opacity-80"
        href="/feature-requests"
      >
        Feature requests
      </a>
      <a
        className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap no-underline hover:opacity-80"
        href="/contact"
      >
        Contact
      </a>
      <a
        className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap no-underline hover:opacity-80"
        href="/support"
      >
        Support
      </a>
      <a
        className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap no-underline hover:opacity-80"
        href="/privacy"
      >
        Privacy
      </a>
      <a
        className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap no-underline hover:opacity-80"
        href="/terms"
      >
        Terms
      </a>
    </div>
  );
}

function Frame16() {
  return (
    <div className="content-stretch flex flex-col sm:flex-row items-center justify-between max-w-[1440px] relative shrink-0 w-full gap-[12px]">
      <Frame19 />
      <div className="content-stretch flex flex-col sm:flex-row items-center gap-[12px] relative shrink-0">
        <LegalLinks />
        <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap">
          &copy; 2026 Evo Collectibles Inc.
        </p>
      </div>
    </div>
  );
}

function Frame15() {
  return (
    <div className="bg-[#ff381a] relative shrink-0 w-full">
      <div className="flex flex-col items-center justify-center size-full">
        <div className="content-stretch flex flex-col items-center justify-center px-[24px] py-[24px] relative w-full box-border">
          <Frame16 />
        </div>
      </div>
    </div>
  );
}

export default function Website() {
  return (
    <div
      className="bg-[#07001a] content-stretch flex flex-col items-center relative size-full overflow-x-hidden"
      data-name="website"
    >
      <FrameHero />
      <Frame3 />
      <Frame15 />
    </div>
  );
}
